import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IActivityComponent } from "@hulanbv/platformapp";
import { classes, stylesheet } from "typestyle";
import { Flex } from "../../../elements/flex.element";
import { Button } from "../../../elements/button.element";
import { InjectionService } from "../../../../state/common/injection.service";

type Props = {
  activityComponents: IActivityComponent[];
  onComplete: (keptValues: string[]) => void;
};

const ValueIdentificationSwipeView: FC<Props> = ({
  onComplete,
  activityComponents,
}) => {
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [cardNumber, setCardNumber] = useState(0);
  const [keptValues, setKeptValues] = useState<string[]>([]);

  const dragStartXRef = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState<number | null>(null);
  const hoveredState = useMemo(() => {
    if ((dragOffset ?? 0) > 50) {
      return "kept";
    }
    if ((dragOffset ?? 0) < -50) {
      return "discarded";
    }

    return null;
  }, [dragOffset]);

  useEffect(() => {
    if (cardNumber >= (activityComponents?.length ?? 0)) {
      onComplete(keptValues);
    }
  }, [cardNumber, activityComponents, keptValues, onComplete]);

  const swipeValue = useCallback(
    (action?: "kept" | "discarded") => {
      // reset drag offset after swipe
      setDragOffset(null);
      dragStartXRef.current = null;

      const chosenAction = action ?? hoveredState;
      const componentValue = activityComponents?.[cardNumber]?.value;
      if (chosenAction !== null && componentValue !== undefined) {
        if (chosenAction === "kept") {
          setKeptValues((curr) => curr.concat(componentValue));
        }

        setCardNumber(cardNumber + 1);
      }
    },
    [activityComponents, cardNumber, hoveredState],
  );

  // touch event handlers for swiping cards
  useEffect(() => {
    const cardContainer = cardContainerRef.current;
    if (!cardContainer) {
      return () => {
        /* noop */
      };
    }
    // on touch start, record starting x position
    const onTouchStart = (event: TouchEvent) => {
      dragStartXRef.current = event.touches[0].clientX;
    };

    // on touch move, calculate drag offset and update state
    const onTouchMove = (event: TouchEvent) => {
      if (dragStartXRef.current === null) {
        return;
      }

      event.preventDefault();

      const currentX = event.changedTouches[0].clientX;
      setDragOffset(currentX - (dragStartXRef.current ?? 0));
    };

    const onMouseDown = (event: MouseEvent) => {
      dragStartXRef.current = event.clientX;
    };

    const onMouseMove = (event: MouseEvent) => {
      if (dragStartXRef.current === null) {
        return;
      }
      event.preventDefault();

      const currentX = event.clientX;
      setDragOffset(currentX - (dragStartXRef.current ?? 0));
    };

    cardContainer.addEventListener("touchstart", onTouchStart);
    cardContainer.addEventListener("touchmove", onTouchMove);
    cardContainer.addEventListener("mousedown", onMouseDown);
    cardContainer.addEventListener("mousemove", onMouseMove);

    return () => {
      cardContainer.removeEventListener("touchstart", onTouchStart);
      cardContainer.removeEventListener("touchmove", onTouchMove);
      cardContainer.removeEventListener("mousedown", onMouseDown);
      cardContainer.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  // separate useEffect to (un)bind touchend since it depends on a changing callback
  useEffect(() => {
    const cardContainer = cardContainerRef.current;
    if (!cardContainer) {
      return () => {
        /* noop */
      };
    }

    // on touch end, determine if swipe was left or right and update card number and kept values accordingly
    const onTouchEnd = (event: TouchEvent) => {
      if (dragStartXRef.current === null) {
        return;
      }
      swipeValue();
    };

    const onMouseUp = (event: MouseEvent) => {
      if (dragStartXRef.current === null) {
        return;
      }
      swipeValue();
    };

    cardContainer.addEventListener("touchend", onTouchEnd);
    cardContainer.addEventListener("mouseup", onMouseUp);

    return () => {
      cardContainer.removeEventListener("touchend", onTouchEnd);
      cardContainer.removeEventListener("mouseup", onMouseUp);
    };
  }, [swipeValue]);

  useEffect(() => {
    if (hoveredState !== null) {
      InjectionService.hapticFeedback("light");
    }
  }, [hoveredState]);

  return (
    <Flex direction="column" alignItems="stretch" flex={1}>
      <Flex direction="column" flex={0}>
        <p className={styles.info}>Very important to you right now? Or nah?</p>
      </Flex>
      <Flex
        flex={1}
        direction="column"
        attributes={{ style: { position: "relative" } }}
        ref={cardContainerRef}
      >
        {activityComponents?.map((component, index) => {
          const position = index - cardNumber;
          let status: "kept" | "discarded" | "undecided" = "undecided";
          if (index < cardNumber) {
            status = keptValues.includes(component.value)
              ? "kept"
              : "discarded";
          }
          const isDragging = index === cardNumber && dragOffset !== null;
          return (
            <div
              key={component.id}
              className={classes(styles.card, styles[status])}
              style={{
                zIndex: activityComponents.length - position,
                transform: `translateY(${position * 15}px) scale(${
                  1 - position * 0.05
                })`,
                opacity: position < 0 ? 0 : 1.33 - position * 0.33,
                pointerEvents: index === cardNumber ? "auto" : "none",

                // unset translate and rotate after decision
                transition: isDragging
                  ? "transform 0.5s"
                  : "transform 0.5s, opacity 1s, translate 0.5s, rotate 0.5s",
                translate: isDragging ? `${dragOffset}px 0` : undefined,
                rotate: isDragging ? undefined : "",
              }}
            >
              {component.value}
            </div>
          );
        })}
      </Flex>

      <Flex justifyContent="space-between" flex={0}>
        <Button
          attributes={{
            style: { width: "auto" },
            onClick: () => swipeValue("discarded"),
          }}
          flavour={hoveredState === "discarded" ? "danger" : "ghost-danger"}
        >
          Discard
        </Button>
        <Button
          attributes={{
            style: { width: "auto" },
            onClick: () => swipeValue("kept"),
          }}
          flavour={hoveredState === "kept" ? "primary" : "ghost-primary"}
        >
          Keep
        </Button>
      </Flex>
      <Flex flex={0}>
        {cardNumber + 1} of {activityComponents?.length ?? 0}
      </Flex>
    </Flex>
  );
};

export { ValueIdentificationSwipeView };

const styles = stylesheet({
  info: {
    fontSize: ".9em",
    color: "rgb(var(--rgb-color-text-tertiary))",
    textAlign: "center",
    margin: 0,
  },

  card: {
    borderRadius: 15,
    boxShadow: "0 2px 5px 0 rgba(var(--rgb-color-black), 0.1)",
    backgroundColor: "rgb(var(--rgb-color-white))",
    height: "90%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    translate: "0 0",
    textAlign: "center",
    padding: 10,
  },

  kept: {
    translate: "100% 0",
    rotate: "20deg",
    transformOrigin: "right bottom",
  },

  discarded: {
    translate: "-100% 0",
    rotate: "-20deg",
    transformOrigin: "left bottom",
  },

  undecided: {},
});
